import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { HStack } from "@/components/ui/hstack";
import { CloseIcon, Icon } from "@/components/ui/icon";
import { ImageBackground } from "@/components/ui/image-background";
import { Input, InputField } from "@/components/ui/input";
import { Menu, MenuItem, MenuItemLabel } from "@/components/ui/menu";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { VStack } from "@/components/ui/vstack";
import {
  getOrganizationControllerFindOneQueryKey,
  getOrganizationControllerListQueryKey,
  useOrganizationControllerUpdate,
  useOrganizationControllerUploadAvatar,
} from "@/generated/api/api";
import { OrganizationDTO } from "@/generated/api/models";
import { useQueryClient } from "@tanstack/react-query";
import {
  Edit,
  MoreHorizontal,
  TriangleAlert,
  Upload,
} from "lucide-react-native";
import { useState } from "react";
import { ImagePicker, ImagePickerProps } from "../image-picker/image.picker";
import { useMyParticipant } from "@/utils/useMyParticipant";
import { uriToBlob } from "@/utils/uri.to.blob";
import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";
import { useMobileBreakpoint } from "@/utils/useMobileBreakpoint";
import { RestaurantMoreMenu } from "./details/restaurant.more.menu";
import { useRouter } from "expo-router";
import { RestaurantActionList } from "./details/restaurant.action.list";

export function RestaurantBanner({
  organization,
}: {
  organization: OrganizationDTO;
}) {
  const [showModal, setShowModal] = useState(false);
  const [orgName, setOrgName] = useState<string>(organization.name);
  const queryClient = useQueryClient();
  const { mutate, isPending: updateIsPending } =
    useOrganizationControllerUpdate({
      mutation: {
        onSuccess: (orgDto) => {
          queryClient.setQueryData(
            getOrganizationControllerFindOneQueryKey(organization.id),
            () => orgDto
          );
          queryClient.setQueryData(
            getOrganizationControllerListQueryKey(),
            (old?: OrganizationDTO[]) => {
              if (!old) {
                return [orgDto];
              }

              const result = [...old];
              const index = result.findIndex((o) => o.id === orgDto.id);

              if (index === -1) {
                queryClient.invalidateQueries({
                  queryKey: getOrganizationControllerListQueryKey(),
                });
                return old;
              }

              result[index] = orgDto;

              return result;
            }
          );
          setShowModal(false);
          setOrgName(orgDto.name);
        },
      },
    });

  const { mutateAsync: uploadImage, isPending: isImageUploading } =
    useOrganizationControllerUploadAvatar();

  const { myParticipant } = useMyParticipant(organization.id);

  const [imagePickerOpened, setImagePickerOpened] =
    useState<ImagePickerProps["type"]>();

  const bp = useMobileBreakpoint();

  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const router = useRouter();

  return (
    <>
      <VStack className="justify-center items-center w-full">
        <ImageBackground
          className="w-full h-44 bg-background-300 rounded-md overflow-hidden"
          source={{ uri: organization.coverImage ?? undefined }}
        >
          {(myParticipant?.role === "ADMIN" ||
            myParticipant?.role === "OWNER") && (
            <HStack className="justify-end items-start h-full p-2">
              <Menu
                placement="bottom"
                offset={5}
                trigger={({ ...triggerProps }) => {
                  return (
                    <Button
                      size="sm"
                      {...triggerProps}
                      isDisabled={isImageUploading}
                    >
                      <ButtonIcon as={Upload} />
                      <ButtonText>Upload images</ButtonText>
                      {isImageUploading && <ButtonSpinner />}
                    </Button>
                  );
                }}
              >
                <MenuItem
                  key="avatar"
                  textValue="Avatar"
                  onPress={() => setImagePickerOpened("avatar")}
                >
                  <MenuItemLabel size="sm">Avatar</MenuItemLabel>
                </MenuItem>
                <MenuItem
                  key="background"
                  textValue="Background"
                  onPress={() => setImagePickerOpened("background")}
                >
                  <MenuItemLabel size="sm">Background</MenuItemLabel>
                </MenuItem>
              </Menu>
            </HStack>
          )}
        </ImageBackground>
        <Avatar
          size="2xl"
          className="border-solid border-8 border-background-0 w-36 h-36 mt-[-80px]"
        >
          <AvatarFallbackText>{organization.name}</AvatarFallbackText>
          <AvatarImage
            source={{ uri: organization.avatarImage ?? undefined }}
          />
        </Avatar>
        <HStack className="items-center gap-2 justify-center">
          <Heading size="2xl">{organization.name}</Heading>

          {(myParticipant?.role === "ADMIN" ||
            myParticipant?.role === "OWNER") &&
            (bp === "mobile" ? (
              <Button
                size="lg"
                variant="link"
                onPress={() => setShowModal(true)}
              >
                <ButtonIcon as={Edit} size="lg" className="pt-1 w-7 h-7" />
              </Button>
            ) : (
              <RestaurantMoreMenu
                onOpen={() => setShowMoreMenu(true)}
                onClose={() => setShowMoreMenu(false)}
                isOpen={showMoreMenu}
                onEditName={() => setShowModal(true)}
                onShowQrs={() =>
                  router.push({
                    pathname: "/(tabs)/orgs/[orgId]/qrcodes",
                    params: {
                      orgId: organization.id,
                    },
                  })
                }
                trigger={(props) => (
                  <Button
                    size="sm"
                    variant="outline"
                    className="px-1 ml-1"
                    {...props}
                  >
                    <ButtonIcon
                      as={MoreHorizontal}
                      size="lg"
                      className="pt-1 w-7 h-7"
                    />
                  </Button>
                )}
              />
            ))}
          <Modal
            isOpen={showModal}
            onClose={() => {
              setShowModal(false);
            }}
            size="md"
          >
            <ModalBackdrop />
            <ModalContent>
              <ModalHeader>
                <Heading size="md" className="text-typography-950">
                  Edit name
                </Heading>
                <ModalCloseButton>
                  <Icon
                    as={CloseIcon}
                    size="md"
                    className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
                  />
                </ModalCloseButton>
              </ModalHeader>
              <ModalBody>
                <Input>
                  <InputField
                    placeholder="Business name"
                    value={orgName}
                    onChangeText={setOrgName}
                  />
                </Input>
              </ModalBody>
              <ModalFooter>
                <ModalFooter className="flex-col items-start">
                  <Button
                    isDisabled={orgName === "" || updateIsPending}
                    onPress={() => {
                      if (orgName) {
                        mutate({
                          organizationId: organization.id,
                          data: {
                            name: orgName,
                          },
                        });
                      }
                    }}
                    className="w-full"
                  >
                    <ButtonText>Save</ButtonText>
                  </Button>
                </ModalFooter>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </HStack>
        {organization.tipsRestricted && (
          <Alert action="muted" variant="solid" className="mt-4">
            <AlertIcon as={TriangleAlert} className="min-w-5" />
            <AlertText>
              Tipping is temporarily unavailable while the business is under
              review. In the meantime, you can still manage and customize your
              look & feel settings.
            </AlertText>
          </Alert>
        )}
        <RestaurantActionList org={organization} />
      </VStack>
      {imagePickerOpened && (
        <ImagePicker
          isOpened={!!imagePickerOpened}
          isCamera={false}
          type={imagePickerOpened}
          onClose={(img) => {
            setImagePickerOpened(undefined);
            if (!img) {
              return;
            }

            const upload = (uriOrBlob: any) => {
              uploadImage(
                {
                  organizationId: organization.id,
                  type: imagePickerOpened,
                  data: {
                    file: uriOrBlob,
                  },
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({
                      queryKey: getOrganizationControllerFindOneQueryKey(
                        organization.id
                      ),
                    });

                    queryClient.invalidateQueries({
                      queryKey: getOrganizationControllerListQueryKey(),
                    });
                  },
                }
              ).catch((e) => console.error(e));
            };

            uriToBlob(img.uri).then(upload);
          }}
        />
      )}
    </>
  );
}
